import React, { Component, useMemo } from 'react';
import { List, Map, Repeat } from 'immutable';
import { AttributeSelect } from '@kineticdata/react';
import { FieldWrapper } from './FieldWrapper';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';
import {
  Status,
  SuggestionsContainer,
  SuggestionAction,
  generateInput,
  generateSingleSelectionsContainer,
  generateSingleSelection,
} from './TypeaheadDefaults';
import { parseRenderOptions } from './utils';
import classNames from 'classnames';

const selectionToValue = selection => selection.get('name');

const Suggestion = ({ suggestion, active }) => (
  <div className={classNames('suggestion', { active })}>
    <div className="large">{suggestion.get('name')}</div>
    <div className="small">{suggestion.get('description')}</div>
  </div>
);

class AttributesComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { added: List(), adding: '', addingValue: '' };
  }

  // returns the number of attribute values that were previously set
  countPrevious = name =>
    this.props.value.get(name).size - this.countAdded(name);

  // returns the number of attribute values that have been added
  countAdded = name => this.state.added.filter(n => n === name).size;

  onRemove = (name, index) => () => {
    const addedIndex = index - this.countPrevious(name);
    if (addedIndex >= 0) {
      this.setState(state => ({
        added: filterWithOccurrences(
          state.added,
          (n, i) => n !== name || i !== addedIndex,
        ),
      }));
    }
    this.props.onChange(this.props.value.deleteIn([name, index]));
  };

  addPair = (name, value) => {
    this.setState(state => ({
      added: state.added.push(name),
      adding: '',
      addingValue: '',
    }));
    this.props.onChange(
      this.props.value.update(name, List(), values => values.push(value)),
    );
  };

  onInputChange = (name, index, isLast) => event => {
    const value = event.target.value;
    if (!isLast) {
      this.props.onChange(
        this.props.value.update(name, List(), values =>
          values.set(index, value),
        ),
      );
    } else {
      if (name) {
        this.addPair(name, value);
      } else {
        this.setState({ addingValue: value });
      }
    }
  };

  onSelectChange = value => attribute => {
    if (attribute) {
      const name = attribute.get('name');
      if (value) {
        this.addPair(name, value);
      } else {
        this.setState({ adding: name });
      }
    } else {
      this.setState({ adding: '' });
    }
  };

  // Create a new onNew function that calls the provided one and passes in a
  // callback that will set the new attribute definition name into the field
  onNew = () => {
    if (typeof this.props.renderOptions.custom.onNew === 'function') {
      this.props.renderOptions.custom.onNew({
        afterComplete: def => {
          if (def) {
            if (this.state.addingValue) {
              this.addPair(def.name, this.state.addingValue);
            } else {
              this.setState({ adding: def.name });
            }
          }
        },
      });
    }
  };

  render() {
    const { renderOptions, components, ...props } = this.props;

    const attributes = mapWithOccurrences(
      props.value
        .keySeq()
        .sort()
        .flatMap(name => Repeat(name, this.countPrevious(name)))
        .concat(this.state.added.push(this.state.adding)),
      (name, index) => ({
        name,
        index,
        value: props.value.getIn([name, index], ''),
      }),
    );
    return (
      <FieldWrapper {...props} renderOptions={renderOptions} fieldset>
        {attributes.size === 1 && !props.enabled
          ? [
              <div
                className={classNames('input-group input-group--map-field', {
                  'input-group-sm': renderOptions.size === 'sm',
                })}
                key="-1"
              >
                <span className="input-group-text">
                  <span className="visually-hidden">
                    Field is disabled and has no value
                  </span>
                  <span className="text-muted">Attribute</span>
                </span>
                <input
                  className={classNames('form-control', {
                    'form-control-sm': renderOptions.size === 'sm',
                  })}
                  disabled={true}
                  type="text"
                  placeholder={renderOptions.placeholder || 'Value'}
                />
              </div>,
            ]
          : []}
        {attributes.map(({ name, value, index }, i) => {
          const isLast = i === attributes.size - 1;
          const attributeDef = props.options.find(
            option => option.get('name') === name,
          );
          return (
            (!isLast || props.enabled) && (
              <div
                className={classNames('input-group input-group--map-field', {
                  'input-group-sm': renderOptions.size === 'sm',
                })}
                key={i}
              >
                {!isLast ? (
                  attributeDef && attributeDef.get('description') ? (
                    <Tooltip content={attributeDef.get('description')}>
                      {({ ref }) => (
                        <span className="input-group-text" ref={ref}>
                          {name}
                        </span>
                      )}
                    </Tooltip>
                  ) : (
                    <span className="input-group-text">{name}</span>
                  )
                ) : (
                  <AttributeSelect
                    components={{
                      Status,
                      Suggestion,
                      SuggestionAction,
                      SuggestionsContainer,
                      ...components,
                    }}
                    options={availableAttributes(props.options, props.value)}
                    onBlur={props.onBlur}
                    onChange={this.onSelectChange(this.state.addingValue)}
                    onFocus={props.onFocus}
                    onNew={renderOptions.custom.onNew ? this.onNew : undefined}
                    value={attributeDef}
                    placeholder="Attribute"
                    form={props.form}
                    invalid={renderOptions.hasErrors}
                  />
                )}

                <input
                  className={classNames('form-control', {
                    'form-control-sm': renderOptions.size === 'sm',
                  })}
                  disabled={!props.enabled}
                  onBlur={props.onBlur}
                  onChange={this.onInputChange(name, index, isLast)}
                  onFocus={props.onFocus}
                  type="text"
                  value={isLast ? this.state.addingValue : value}
                  placeholder={renderOptions.placeholder || 'Value'}
                  form={props.form}
                />
                {!isLast &&
                  props.enabled && (
                    <button
                      className="btn btn-table"
                      type="button"
                      onFocus={props.onFocus}
                      onBlur={props.onBlur}
                      onClick={this.onRemove(name, index)}
                    >
                      <Icon name="trash" md />
                    </button>
                  )}
              </div>
            )
          );
        })}
      </FieldWrapper>
    );
  }
}

export const AttributesField = props => {
  const renderOptions = parseRenderOptions(props, { isFloating: false });
  const components = useMemo(
    () => ({
      Input: generateInput({
        size: renderOptions.size,
        className: renderOptions.fieldClassName,
      }),
      Selection: generateSingleSelection(selectionToValue, {
        size: renderOptions.size,
        title: true,
      }),
      SelectionsContainer: generateSingleSelectionsContainer({
        size: renderOptions.size,
      }),
    }),
    [renderOptions.size, renderOptions.fieldClassName],
  );

  return (
    <AttributesComponent
      renderOptions={renderOptions}
      components={components}
      {...props}
    />
  );
};

// Helper function that maps for a List but instead of providing the index in
// the overall list, it gives the lambda the count of the occurrences of current
// value that have already occurred in the list.
// For example: ['red', 'green', ''red',]
// Would call: ['red,' 0], ['green', 0], ['red', 1], ['blue', 0]
const mapWithOccurrences = (list, mapper) =>
  list.reduce(
    ([result, counts], current) => [
      result.push(mapper(current, counts.get(current, 0))),
      counts.update(current, 0, count => count + 1),
    ],
    [List(), Map()],
  )[0];

// Same idea as mapWithOccurrences above.
const filterWithOccurrences = (list, pred) =>
  list.reduce(
    ([result, counts], current) => [
      pred(current, counts.get(current, 0)) ? result.push(current) : result,
      counts.update(current, 0, count => count + 1),
    ],
    [List(), Map()],
  )[0];

const availableAttributes = (options, value) =>
  options.filter(
    option =>
      option.get('allowsMultiple') ||
      value.get(option.get('name'), List()).isEmpty(),
  );
